import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Flame, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  ArrowRight,
  Users,
  AlertTriangle,
  MapPin,
  Shield,
  Siren
} from "lucide-react";
import { LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  Flame,
  MapPin,
  Siren,
  Shield
};

export default function FireSafety() {
  const { t, i18n } = useTranslation('training-pages');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const courseDetails = t('fireSafety.courseDetails', { returnObjects: true }) as {
    duration: string;
    certification: string;
    participants: string;
    location: string;
  };

  // Training types - using translation if available, otherwise defaults
  const defaultTrainingTypes = isRTL ? [
    {
      title: "הדרכת בטיחות אש בסיסית",
      duration: "4 שעות",
      audience: "כלל העובדים",
      topics: ["זיהוי מפגעי אש", "שימוש במטפים", "נהלי פינוי", "התנהגות בשריפה"]
    },
    {
      title: "הדרכת צוות כיבוי ראשוני",
      duration: "8 שעות",
      audience: "צוותי כיבוי מיומנים",
      topics: ["שימוש מתקדם במטפים", "טכניקות כיבוי", "הפעלת זרנוקי כיבוי", "חילוץ בסיסי"]
    },
    {
      title: "הדרכת מפקדי פינוי",
      duration: "6 שעות",
      audience: "אחראי קומות/אזורים",
      topics: ["ניהול פינוי", "ספירת נוכחות", "תיאום עם גורמי חירום", "קבלת החלטות בלחץ"]
    }
  ] : [
    {
      title: "Basic Fire Safety Training",
      duration: "4 hours",
      audience: "All employees",
      topics: ["Fire hazard identification", "Use of extinguishers", "Evacuation procedures", "Behavior during fire"]
    },
    {
      title: "Initial Firefighting Team Training",
      duration: "8 hours",
      audience: "Trained firefighting teams",
      topics: ["Advanced extinguisher use", "Firefighting techniques", "Fire hose operation", "Basic rescue"]
    },
    {
      title: "Evacuation Commander Training",
      duration: "6 hours",
      audience: "Floor/area supervisors",
      topics: ["Evacuation management", "Attendance counting", "Coordination with emergency services", "Decision making under pressure"]
    }
  ];

  const defaultPracticalExercises = isRTL ? [
    { icon: "Flame", title: "תרגול כיבוי אש חי", description: "תרגול עם מטפים על אש אמיתית בסביבה מבוקרת" },
    { icon: "MapPin", title: "תרגיל פינוי", description: "סימולציה מלאה של פינוי המבנה עם תסריטים שונים" },
    { icon: "Siren", title: "הפעלת מערכות", description: "הכרת מערכות הגילוי, הכיבוי והאזעקה במבנה" },
    { icon: "Shield", title: "שימוש בציוד", description: "תרגול שימוש בציוד כיבוי וציוד מגן" }
  ] : [
    { icon: "Flame", title: "Live Fire Fighting Practice", description: "Practice with extinguishers on real fire in controlled environment" },
    { icon: "MapPin", title: "Evacuation Drill", description: "Full simulation of building evacuation with various scenarios" },
    { icon: "Siren", title: "System Operation", description: "Familiarization with detection, firefighting and alarm systems" },
    { icon: "Shield", title: "Equipment Use", description: "Practice using firefighting equipment and protective gear" }
  ];

  const defaultLegalRequirements = isRTL ? [
    "חובת הדרכת בטיחות אש לכל עובד",
    "חידוש הדרכה אחת לשנה",
    "תרגיל פינוי שנתי לפחות",
    "מינוי אחראי כיבוי אש",
    "תחזוקת ציוד כיבוי",
    "תכנית פינוי מעודכנת"
  ] : [
    "Fire safety training required for all employees",
    "Annual training renewal",
    "At least one annual evacuation drill",
    "Appointment of fire safety officer",
    "Firefighting equipment maintenance",
    "Updated evacuation plan"
  ];

  const trainingTypes = defaultTrainingTypes;
  const practicalExercises = defaultPracticalExercises;
  const legalRequirements = defaultLegalRequirements;

  const audienceLabel = isRTL ? "קהל יעד" : "Target audience";
  const legalSubtitle = isRTL ? "חובות רגולטוריות" : "Regulatory obligations";

  return (
    <Layout>
      <Helmet>
        <title>{t('fireSafety.meta.title')}</title>
        <meta name="description" content={t('fireSafety.meta.description')} />
        <link rel="canonical" href="https://adam-safety.com/training/fire-safety" />
        <meta property="og:title" content={t('fireSafety.meta.title')} />
        <meta property="og:description" content={t('fireSafety.meta.description')} />
        <meta property="og:url" content="https://adam-safety.com/training/fire-safety" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('fireSafety.meta.title')} />
        <meta name="twitter:description" content={t('fireSafety.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <Flame className="w-5 h-5" />
              <span className="text-sm font-medium">{t('common.safetyTraining')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {t('fireSafety.hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('fireSafety.hero.subtitle')}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Users className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.participants}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.location}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('common.orderTraining')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Training Types */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('fireSafety.trainingTypes.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('fireSafety.trainingTypes.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {trainingTypes.map((training, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm px-3 py-1 rounded-full bg-accent/20 text-accent">
                    {training.duration}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{training.title}</h3>
                <p className="text-sm text-accent mb-4">{audienceLabel}: {training.audience}</p>
                <ul className="space-y-2">
                  {training.topics.map((topic, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">{topic}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Practical Exercises */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('fireSafety.practicalExercises.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('fireSafety.practicalExercises.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {practicalExercises.map((exercise, index) => {
              const IconComponent = iconMap[exercise.icon] || Shield;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-primary rounded-2xl p-6 text-primary-foreground text-center"
                >
                  <IconComponent className="w-12 h-12 text-accent mx-auto mb-4" />
                  <h3 className="font-bold mb-2">{exercise.title}</h3>
                  <p className="text-primary-foreground/80 text-sm">{exercise.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('common.legalRequirementsTitle')}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto bg-red-50 dark:bg-red-900/20 rounded-3xl p-8 border border-red-200 dark:border-red-800"
          >
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
              <h3 className="text-xl font-bold text-red-700 dark:text-red-400">{legalSubtitle}</h3>
            </div>
            <ul className="space-y-3">
              {legalRequirements.map((req, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <span className="text-red-800 dark:text-red-200">{req}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t('fireSafety.cta.title')}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {t('fireSafety.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('common.orderTraining')}
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {t('common.moreTrainings')}
                  <ArrowIcon className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
